import { forwardRef, useRef, type JSX, type ReactNode } from 'react';

import { PieChartLegend } from './PieChartLegend/PieChartLegend.js';
import { PieChartOverlays } from './PieChartOverlays/PieChartOverlays.js';
import { PieChartPlot } from './PieChartPlot/PieChartPlot.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { ChartLayout } from '../../core/components/chart-layout/ChartLayout.js';
import { useAutoLegendRefresh } from '../../core/components/chart-layout/hooks/useAutoLegendRefresh.js';
import { OverrideRatioFunction } from '../../core/components/chart-layout/types/chart-layout.js';
import { ORIENTATION } from '../../core/components/chart-layout/types/constants.js';
import { LEGEND_POSITION } from '../../core/constants.js';
import type { AriaLabel } from '../../core/types/aria-label.js';
import { sanitizeLegendRatio } from '../../core/utils/sanitize-legend-ratio.js';
import { useChartLegend } from '../context/ChartLegendContext.js';
import { PieChartLoadingContext } from '../context/PieChartLoadingContext.js';
import { usePieChartRawData } from '../context/PieChartRawDataContext.js';
import { PieChartSlicesDataProvider } from '../providers/PieChartSlicesDataProvider.js';
import {
  DEFAULT_CONTAINER_PADDING,
  DEFAULT_SIZE,
  PIE_CATEGORICAL_LEGEND_SIZES,
} from '../types/default.js';
import type { ChartVariant } from '../types/internal/pie-chart.js';
import { PieChartProps } from '../types/pie-chart.config.js';

type PieChartInternalProps = Omit<PieChartProps, 'data'> &
  AriaLabel & {
    variant: ChartVariant;
    height?: string | number;
    width?: number;
    isEmpty?: boolean;
    emptyState?: ReactNode;
    errorState?: ReactNode | ((message: string) => JSX.Element);
  };

export const PieChartInternal = forwardRef<
  HTMLDivElement,
  PieChartInternalProps
>((props, forwardedRef) => {
  const {
    height,
    width,
    isEmpty,
    emptyState,
    errorState,
    loading = false,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
  } = props;
  const {
    hidden: legendHidden,
    position: legendPosition,
    ratio,
    onRatioChange,
  } = useChartLegend();
  const plotAreaRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs([forwardedRef, plotAreaRef]);

  const chartData = usePieChartRawData();

  const overrideRatio: OverrideRatioFunction = (orientation, width, height) => {
    const sanitizedRatio = sanitizeLegendRatio(ratio);

    const isHorizontal = orientation === ORIENTATION.Horizontal;
    const legendHeight = isHorizontal ? height : sanitizedRatio * height * 0.01;
    const legendWidth = isHorizontal ? width - height : width;

    return {
      width: legendWidth,
      height: legendHeight,
    };
  };

  const chartLayoutRef = useAutoLegendRefresh(chartData);

  return (
    <PieChartLoadingContext.Provider value={loading}>
      <ChartLayout
        ref={chartLayoutRef}
        chartWidth={width ?? '100%'}
        chartHeight={height ?? DEFAULT_SIZE}
        isEmpty={isEmpty}
        emptyState={emptyState}
        errorState={errorState}
        showLoader={loading}
        className={className}
        style={{ padding: `0 ${DEFAULT_CONTAINER_PADDING}`, ...style }}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        data-testid={dataTestId}
        aria-label={ariaLabel}
      >
        <ChartLayout.Provider provider={PieChartSlicesDataProvider}>
          <ChartLayout.Graph>
            <PieChartOverlays>
              <PieChartPlot {...props} ref={mergedRef} />
            </PieChartOverlays>
          </ChartLayout.Graph>
          {!legendHidden && (
            <ChartLayout.Legend
              position={legendPosition}
              ratio={
                legendPosition === LEGEND_POSITION.Bottom ? ratio : undefined
              }
              ratioBoundaries={PIE_CATEGORICAL_LEGEND_SIZES}
              overrideRatio={
                legendPosition !== LEGEND_POSITION.Bottom
                  ? overrideRatio
                  : undefined
              }
              onResize={onRatioChange}
            >
              <PieChartLegend />
            </ChartLayout.Legend>
          )}
        </ChartLayout.Provider>
      </ChartLayout>
    </PieChartLoadingContext.Provider>
  );
});

(
  PieChartInternal as typeof PieChartInternal & { displayName: string }
).displayName = 'PieChartInternal';
